import type { BrandUser, RegistrationData, RegistrationStepTwoData } from '@/types'
import type { AxiosResponse } from 'axios'
import useAccessStore from '../store/accessStore'
import { API_ENDPOINTS } from '../utils/apiEndpoints'
import { logError } from '../utils/sentry'
import api from './api'

class BrandUserService {
  async getBrandUser(): Promise<BrandUser> {
    try {
      const response: AxiosResponse<BrandUser> = await api.get(API_ENDPOINTS.BRAND.BASE)
      return response.data
    } catch (error) {
      logError('Error fetching brand user:', { error })
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to fetch brand user: ${errorMessage}`)
    }
  }

  async updateBrandUser(userData: Partial<BrandUser>): Promise<BrandUser> {
    try {
      const response: AxiosResponse<BrandUser> = await api.put(API_ENDPOINTS.BRAND.BASE, userData)
      return response.data
    } catch (error) {
      logError('Error updating brand user:', { error })
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to update brand user: ${errorMessage}`)
    }
  }

  async registerStepOne(userData: RegistrationData): Promise<{ userId: string }> {
    try {
      const response = await api.post(API_ENDPOINTS.BRAND.REGISTER_STEP_ONE, userData)

      if (response.data?.id) {
        return { userId: String(response.data.id) }
      }

      return { userId: `temporary-id-${new Date().getTime()}` }
    } catch (error) {
      console.error('Registration step one error:', error)

      throw error
    }
  }

  async registerStepTwo(brandData: RegistrationStepTwoData): Promise<BrandUser> {
    try {
      const response: AxiosResponse<BrandUser> = await api.post(API_ENDPOINTS.BRAND.REGISTER_STEP_TWO, brandData)

      if (response.status === 200) {
        try {
          useAccessStore.getState().setValue('registrationStatus', 'completed')
          await useAccessStore.getState().fetchBrandData()
        } catch (error) {
          console.error('Error updating registration status:', error)
        }
      }
      return response.data
    } catch (error) {
      logError('Error in registration step two:', { error })
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Registration step two failed: ${errorMessage}`)
    }
  }

  async uploadBrandLogo(file: File): Promise<string> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post(API_ENDPOINTS.BRAND.LOGO_IMAGE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.status === 200) {
        try {
          const refreshed = await useAccessStore.getState().refreshTokens()
          if (refreshed) {
            try {
              await useAccessStore.getState().fetchBrandData()
            } catch (_e) {
              // Non-fatal: logo URL may still be returned below
            }
          }
        } catch (_e) {
          // refreshTokens already handles auth state on failure; proceed to return response data
        }

        if (response.data?.url) {
          return response.data.url
        }

        if (typeof response.data === 'string') {
          return response.data
        }

        if (response.data && typeof response.data === 'object') {
          const possibleUrlProps = ['imageUrl', 'logoUrl', 'path', 'location', 'link']
          for (const prop of possibleUrlProps) {
            if (response.data[prop] && typeof response.data[prop] === 'string') {
              return response.data[prop]
            }
          }

          return 'Logo uploaded successfully'
        }

        return 'Logo uploaded successfully'
      }

      logError('Logo upload response missing URL')
      return 'Logo uploaded successfully'
    } catch (error) {
      logError('Error uploading brand logo:', { error })
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to upload brand logo: ${errorMessage}`)
    }
  }
}

export default new BrandUserService()
